"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.ButtonOrLink = void 0;
var _react = _interopRequireWildcard(require("react"));
var _useIsoEffect = require("../hooks/useIsoEffect");
var _reakitUtils = require("./reakit-utils");
const _excluded = ["children", "to", "href", "rel", "target", "type", "disabled", "focusable", "tabIndex", "style", "onKeyDown", "onKeyUp", "onKeyPressCapture", "onClickCapture", "onMouseDown", "onMouseDownCapture"];
/**
 * ButtonOrLink is adapted from the reakit Button, Clickable, and Tabbable source code:
 * - https://github.com/ariakit/ariakit/blob/reakit/packages/reakit/src/Button/Button.ts
 * - https://github.com/ariakit/ariakit/blob/reakit/packages/reakit/src/Clickable/Clickable.ts
 * - https://github.com/ariakit/ariakit/blob/reakit/packages/reakit/src/Tabbable/Tabbable.ts
 */
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
const ButtonOrLink = /*#__PURE__*/(0, _react.memo)( /*#__PURE__*/(0, _react.forwardRef)((_ref, ref) => {
  let children = _ref.children,
    to = _ref.to,
    href = _ref.href,
    rel = _ref.rel,
    target = _ref.target,
    _ref$type = _ref.type,
    type = _ref$type === void 0 ? 'button' : _ref$type,
    disabled = _ref.disabled,
    focusable = _ref.focusable,
    tabIndex = _ref.tabIndex,
    style = _ref.style,
    onKeyDown = _ref.onKeyDown,
    onKeyUp = _ref.onKeyUp,
    onKeyPressCapture = _ref.onKeyPressCapture,
    onClickCapture = _ref.onClickCapture,
    onMouseDown = _ref.onMouseDown,
    onMouseDownCapture = _ref.onMouseDownCapture,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const elementRef = (0, _react.useRef)(null);
  (0, _react.useImperativeHandle)(ref, () => elementRef.current, []); // Merges forwarded ref with internal elementRef

  const isLink = to || href;
  const trulyDisabled = Boolean(disabled && !focusable);
  const _useState = (0, _react.useState)(true),
    nativeTabbable = _useState[0],
    setNativeTabbable = _useState[1];
  const _useState2 = (0, _react.useState)(true),
    supportsDisabled = _useState2[0],
    setSupportsDisabled = _useState2[1];
  const _useState3 = (0, _react.useState)(false),
    active = _useState3[0],
    setActive = _useState3[1];
  const isActiveRef = (0, _react.useRef)(false);

  // Evaluate element to set state values used to compute appropriate tabIndex
  (0, _useIsoEffect.useIsoEffect)(() => {
    const element = elementRef.current;
    if (!element) return;
    if (!(0, _reakitUtils.isNativeTabbable)(element)) setNativeTabbable(false);
    if (!(0, _reakitUtils.supportsDisabledAttribute)(element)) setSupportsDisabled(false);
  }, []);

  // Handle Enter and Spacebar keydown events
  const handleOnKeyDown = (0, _react.useCallback)(event => {
    onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(event);
    if (event.defaultPrevented || disabled || event.metaKey || event.target !== event.currentTarget) return;
    const isEnter = event.key === 'Enter';
    const isSpace = event.key === ' ';
    if (isEnter || isSpace) {
      if ((0, _reakitUtils.isNativeClick)(event)) return;
      event.preventDefault();
      // Trigger a click on Enter keydown
      if (isEnter) event.currentTarget.click();
      // Set data-active attribute on Spacebar keydown, and prepare to trigger click on Spacebar keyup
      else if (isSpace && !isActiveRef.current) {
        isActiveRef.current = true;
        setActive(true);
      }
    }
  }, [disabled, onKeyDown]);

  // Handle Spacebar keyup event
  const handleOnKeyUp = (0, _react.useCallback)(event => {
    onKeyUp === null || onKeyUp === void 0 ? void 0 : onKeyUp(event);
    if (event.defaultPrevented || disabled || event.metaKey) return;

    // Remove data-active attribute on Spacebar keyup and trigger a click
    if (isActiveRef.current && event.key === ' ') {
      isActiveRef.current = false;
      setActive(false);
      event.currentTarget.click();
    }
  }, [disabled, onKeyUp]);
  const handleOnClickCapture = (0, _react.useCallback)(event => {
    onClickCapture === null || onClickCapture === void 0 ? void 0 : onClickCapture(event);
    if (event.defaultPrevented) return;
    if (disabled) {
      event.stopPropagation();
      event.preventDefault();
    }
  }, [disabled, onClickCapture]);
  const handleOnMouseDownCapture = (0, _react.useCallback)(event => {
    onMouseDownCapture === null || onMouseDownCapture === void 0 ? void 0 : onMouseDownCapture(event);
    if (event.defaultPrevented) return;
    if (disabled) {
      event.stopPropagation();
      event.preventDefault();
    }
  }, [disabled, onMouseDownCapture]);
  const handleOnKeyPressCapture = (0, _react.useCallback)(event => {
    onKeyPressCapture === null || onKeyPressCapture === void 0 ? void 0 : onKeyPressCapture(event);
    if (event.defaultPrevented) return;
    if (disabled) {
      event.stopPropagation();
      event.preventDefault();
    }
  }, [disabled, onKeyPressCapture]);

  // Handle Button focus on mousedown in Safari or Firefox on Mac browsers
  const handleOnMouseDown = (0, _react.useCallback)(event => {
    onMouseDown === null || onMouseDown === void 0 ? void 0 : onMouseDown(event);
    if (event.defaultPrevented) return;
    (0, _reakitUtils.handleButtonFocusOnSafariOrFirefoxOnMac)(event);
  }, [onMouseDown]);
  const accessibilityProps = (0, _react.useMemo)(() => ({
    disabled: trulyDisabled && supportsDisabled ? true : undefined,
    'aria-disabled': disabled ? true : undefined,
    tabIndex: (0, _reakitUtils.getTabIndex)(trulyDisabled, nativeTabbable, supportsDisabled, tabIndex)
  }), [disabled, trulyDisabled, supportsDisabled, nativeTabbable, tabIndex]);
  const accessibilityStyles = (0, _react.useMemo)(() => Object.assign({
    pointerEvents: disabled ? 'none' : undefined
  }, style), [disabled, style]);
  if (isLink) {
    return /*#__PURE__*/_react.default.createElement("a", _extends({}, props, accessibilityProps, {
      ref: elementRef,
      "data-active": active || undefined,
      href: to !== null && to !== void 0 ? to : href,
      onClickCapture: handleOnClickCapture,
      onKeyDown: handleOnKeyDown,
      onKeyPressCapture: handleOnKeyPressCapture,
      onKeyUp: handleOnKeyUp,
      onMouseDown: handleOnMouseDown,
      onMouseDownCapture: handleOnMouseDownCapture,
      rel: !rel && target === '_blank' ? 'noopener noreferrer' : rel,
      style: accessibilityStyles,
      target: target
    }), children);
  }
  return /*#__PURE__*/_react.default.createElement("button", _extends({}, props, accessibilityProps, {
    ref: elementRef,
    "data-active": active || undefined,
    onClickCapture: handleOnClickCapture,
    onKeyDown: handleOnKeyDown,
    onKeyPressCapture: handleOnKeyPressCapture,
    onKeyUp: handleOnKeyUp,
    onMouseDown: handleOnMouseDown,
    onMouseDownCapture: handleOnMouseDownCapture,
    style: accessibilityStyles
    // eslint-disable-next-line react/button-has-type
    ,
    type: type
  }), children);
}));
exports.ButtonOrLink = ButtonOrLink;
ButtonOrLink.displayName = 'ButtonOrLink';